// <reference path="../types/backend-2.d.ts" />
import { hc, type InferRequestType } from "hono/client";
import { createEffect, createSignal } from "solid-js";
import {
  authorizationSignal,
  getUser,
  logout,
  resetToken,
} from "./authorization";

import type { APIType } from "@road-rally/backend-api/api-types";

// Load API URL from env var
const API_URL = import.meta.env.PUBLIC_SERVER_URL;
console.log("API_URL", API_URL);
if (!API_URL) {
  throw new Error("API_URL is not defined in the environment");
}

const [auth] = authorizationSignal;
const createClient = (auth: string | undefined | null) => {
  // TODO - Replace domain w/ an env var
  return {
    // Note... this looks really odd, but is needed to ensure that solid and hono's
    // proxies don't interfere with each other
    api: hc<APIType>(API_URL, {
      headers: auth ? { Authorization: `Bearer ${auth}` } : {},
    }).api,
  };
};

const [client, setClient] = createSignal(createClient(auth()));

createEffect(() => {
  // Update the client with the new auth header (if any)
  setClient(createClient(auth()));
});

type APIEndpoint = ReturnType<typeof client>["api"];

const createApiRoutes = () => ({
  api: client,
  dev: {
    wipeEvents: () =>
      client()
        .api.admin.dev["wipe-events"].$post()
        .then(() => {
          return;
        }),
  },
  loggedIn: () => {
    const [auth] = authorizationSignal;
    return Boolean(auth());
  },
  user: getUser,
  getUserName: (p: { userId: number }) =>
    client()
      .api.public.users[":userId"].$get({
        param: { userId: `${p.userId}` },
      })
      .then((r) => {
        if (r.ok) return r.json();
        throw new Error(r.statusText);
      }),
  logout,
  login: (
    p: InferRequestType<APIEndpoint["public"]["login"]["$post"]>["json"]
  ) =>
    client()
      .api.public.login.$post({
        json: p,
      })
      .then(async (r) => {
        if (r.ok) {
          const d = await r.json();
          resetToken(d.token ?? "");
          return d;
        }
        throw new Error(r.statusText);
      }),
  resetToken,
  verifyJWT: async (
    p: InferRequestType<
      APIEndpoint["public"]["jwt-verifications"]["$post"]
    >["json"]
  ) =>
    client()
      .api.public["jwt-verifications"].$post({ json: p })
      .then(async (r) => {
        if (r.ok) {
          const d = await r.json();
          if (d.user) resetToken(p.jwt);
          return d;
        }
        throw new Error(r.statusText);
      }),
  contact: (
    p: InferRequestType<APIEndpoint["public"]["admin-contact"]["$post"]>["json"]
  ) =>
    client().api.public["admin-contact"].$post({
      json: p,
    }),
  copyImageForCompression: (
    p: InferRequestType<
      APIEndpoint["public"]["image-copy-for-compression"]["$post"]
    >["json"]
  ) =>
    client()
      .api["public"]["image-copy-for-compression"].$post({
        json: p,
      })
      .then(async (r) => {
        if (r.ok) return r.json();
        throw new Error(r.statusText);
      }),
  admin: {
    getFinanceReport: (
      p: InferRequestType<
        APIEndpoint["admin"]["reports"]["event"]["$get"]
      >["query"]
    ) =>
      client()
        .api.admin.reports.event.$get({
          query: p,
        })
        .then(async (r) => {
          if (r.ok) {
            return r.json();
          }
          throw new Error(r.statusText);
        }),
    getTimeReport: (
      p: InferRequestType<
        APIEndpoint["admin"]["reports"]["time"]["$get"]
      >["query"]
    ) =>
      client()
        .api.admin.reports.time.$get({
          query: p,
        })
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
    getUserSessions: (p: { start: number; end: number }) =>
      client()
        .api.admin["sessions"]["user-sessions"].$get({
          query: { start: p.start.toString(), end: p.end.toString() },
        })
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
    getEventDropoff: (p: { eventId: number }) =>
      client()
        .api.admin["sessions"]["event-registration-dropoff-report"][
          ":eventId"
        ].$get({
          param: { eventId: p.eventId.toString() },
        })
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
    getQSPData: (p: {
      qsp: string;
      eventId?: number;
      filters: {
        includeKnownUsers: boolean;
        includeNewUsers: boolean;
        useRegEventsOnly: boolean;
      };
    }) =>
      client()
        .api.admin["sessions"]["qsp-report"].$post({
          json: { qsp: p.qsp, eventId: p.eventId, filters: p.filters },
        })
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
    emails: {
      blacklist: (p: { email: string; delete?: boolean }) =>
        client()
          .api.admin["blocked-emails"][":email"].$post({
            param: { email: p.email },
            json: {
              delete: p.delete,
              blacklist: true,
            },
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
      getBlacklist: () =>
        client()
          .api.admin["blocked-emails"][":email"].$post({
            param: { email: "a@b.co" },
            json: {},
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
    },
  },
  cart: {
    get: () =>
      client()
        .api.user.cart.$get()
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
    add: (p: InferRequestType<APIEndpoint["user"]["cart"]["$put"]>["json"]) =>
      client()
        .api.user.cart.$put({
          json: p,
        })
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
    edit: (p: InferRequestType<APIEndpoint["user"]["cart"]["$put"]>["json"]) =>
      client()
        .api.user.cart.$put({
          json: p,
        })
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
    delete: (
      p: InferRequestType<APIEndpoint["user"]["cart"]["$delete"]>["query"]
    ) =>
      client()
        .api.user.cart.$delete({
          query: p,
        })
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
  },
  products: {
    get: () =>
      client()
        .api.public.products.$get()
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
    byId: (productId: number) => ({
      get: () =>
        client()
          .api.public.products[":productId"].$get({
            param: {
              productId: `${productId}`,
            },
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
    }),
    buy: (
      p: InferRequestType<
        APIEndpoint["user"]["payments"]["products"]["$post"]
      >["json"]
    ) =>
      client()
        .api.user.payments.products.$post({
          json: p,
        })
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
    admin: {
      create: (
        p: InferRequestType<APIEndpoint["admin"]["products"]["$post"]>["json"]
      ) =>
        client()
          .api.admin.products.$post({
            json: p,
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
      update: (
        p: InferRequestType<
          APIEndpoint["admin"]["products"][":productId"]["$patch"]
        >["json"] & {
          id: number;
        }
      ) =>
        client()
          .api.admin.products[":productId"].$patch({
            param: { productId: `${p.id}` },
            json: p,
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
      delete: (p: { id: number }) =>
        client()
          .api.admin.products[":productId"].$delete({
            param: { productId: `${p.id}` },
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
      get: () => client().api.admin.products.$get(),
      byId: (productId: number) => ({
        deleteImage: (p: { image: string }) =>
          client()
            .api.admin.products[":productId"].images[":image"].$delete({
              param: {
                image: p.image,
                productId: `${productId}`,
              },
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
        uploadImage: (
          p: InferRequestType<
            APIEndpoint["admin"]["products"][":productId"]["images"]["$post"]
          >["json"]
        ) =>
          client()
            .api.admin.products[":productId"].images.$post({
              param: { productId: `${productId}` },
              json: p,
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
      }),
    },
  },
  users: {
    admin: {
      list: (
        p: InferRequestType<APIEndpoint["admin"]["users"]["$get"]>["query"]
      ) =>
        client()
          .api.admin.users.$get({ query: p })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
      byId: (userId: number) => ({
        getEventReport: () =>
          client()
            .api.admin.users[":userId"]["event-report"].$get({
              param: { userId: `${userId}` },
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
        delete: () =>
          client().api.admin.users[":userId"].$delete({
            param: { userId: `${userId}` },
          }),
        changeType: (
          p: InferRequestType<
            APIEndpoint["admin"]["users"][":userId"]["$patch"]
          >["json"]
        ) =>
          client()
            .api.admin.users[":userId"].$patch({
              param: { userId: `${userId}` },
              json: p,
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
      }),
    },
    create: (
      p: InferRequestType<
        APIEndpoint["public"]["create-account"]["$post"]
      >["json"]
    ) =>
      client()
        .api.public["create-account"].$post({
          json: p,
        })
        .then(async (r) => {
          if (r.ok) {
            return r.json();
          }
          throw new Error(r.statusText);
        }),
    createTemp: (
      p: InferRequestType<
        APIEndpoint["public"]["create-temp-user"]["$post"]
      >["json"]
    ) =>
      client()
        .api.public["create-temp-user"].$post({
          json: p,
        })
        .then(async (r) => {
          if (r.ok) {
            return r.json();
          }
          const message = await r.text();
          throw new Error(message);
        }),
    delete: (
      p: InferRequestType<APIEndpoint["user"]["current-user"]["$delete"]>
    ) =>
      client()
        .api.user["current-user"].$delete({
          json: p,
        })
        .then(async (r) => {
          if (r.ok) {
            return r.json();
          }
          throw new Error(r.statusText);
        }),
    sendPasswordReset: (
      p: InferRequestType<
        APIEndpoint["public"]["send-password-reset"]["$post"]
      >["json"]
    ) =>
      client()
        .api.public["send-password-reset"].$post({
          json: p,
        })
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
    sendVerifyEmail: (
      p: InferRequestType<
        APIEndpoint["public"]["send-email-verification"]["$post"]
      >["json"]
    ) =>
      client()
        .api.public["send-email-verification"].$post({
          json: p,
        })
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
    validateEmailPin: (
      p: InferRequestType<
        APIEndpoint["public"]["complete-email-verification"]["$post"]
      >["json"]
    ) =>
      client()
        .api.public["complete-email-verification"].$post({
          json: p,
        })
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
    updateProfile: (
      p: InferRequestType<
        APIEndpoint["user"]["current-user"]["profile"]["$patch"]
      >["json"]
    ) =>
      client()
        .api.user["current-user"].profile.$patch({
          json: p,
        })
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
    validateResetPin: (
      p: InferRequestType<
        APIEndpoint["public"]["verify-reset-password"]["$post"]
      >["json"]
    ) =>
      client()
        .api.public["verify-reset-password"].$post({
          json: p,
        })
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
    changePassword: (
      p: InferRequestType<
        APIEndpoint["user"]["current-user"]["passwords"]["$patch"]
      >["json"]
    ) =>
      client()
        .api.user["current-user"].passwords.$patch({
          json: p,
        })
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
    resetPasswordWithPin: (
      p: InferRequestType<
        APIEndpoint["public"]["complete-reset-password"]["$post"]
      >["json"]
    ) =>
      client()
        .api.public["complete-reset-password"].$post({
          json: p,
        })
        .then((r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),

    get: () =>
      client()
        .api.user["current-user"].$get()
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
    events: {
      getRecent: () =>
        client()
          .api.user["recent-events"].$get()
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
      getPast: () =>
        client()
          .api.user["past-events"].$get()
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
      getRegistered: () =>
        client()
          .api.user["registered-events"].$get()
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
    },
  },
  gallery: {
    get: (p: { uploadDate?: Date }) =>
      client()
        .api.admin["gallery-content"].$get({
          query: {
            uploadDate: p.uploadDate?.toString(),
          },
        })
        .then((r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
    uploadImage: (
      p: InferRequestType<
        APIEndpoint["admin"]["gallery-images"]["$post"]
      >["json"]
    ) =>
      client()
        .api.admin["gallery-images"].$post({ json: p })
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
    uploadVideo: (
      p: InferRequestType<
        APIEndpoint["admin"]["gallery-videos"]["$post"]
      >["json"]
    ) =>
      client()
        .api.admin["gallery-videos"].$post({ json: p })
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
    deleteVideo: (p: { link: string }) =>
      client()
        .api.admin["gallery-videos"].$delete({
          query: p,
        })
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
    deleteImage: (p: { image: string }) =>
      client()
        .api.admin["gallery-images"][":imageId"].$delete({
          param: {
            imageId: p.image,
          },
        })
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
  },
  locations: {
    byId: (locationId: number) => ({
      get: () =>
        client()
          .api.public.locations[":locationId"].$get({
            param: { locationId: `${locationId}` },
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
    }),
  },
  events: {
    get: (p: { past?: boolean; page?: number }) =>
      client()
        .api.public.events.$get({
          query: {
            page: p.page?.toString(),
            past: p.past ? "1" : "",
          },
        })
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
    getAnnouncements: (p: { eventId: number }) =>
      client()
        .api.public.announcements.$get({
          query: { eventIds: p.eventId.toString() },
        })
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
    create: (
      p: InferRequestType<APIEndpoint["admin"]["events"]["$post"]>["json"]
    ) =>
      client()
        .api.admin.events.$post({ json: p })
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
    createROD: (
      p: InferRequestType<
        APIEndpoint["admin"]["playable-past-events"]["$post"]
      >["json"]
    ) =>
      client()
        .api.admin["playable-past-events"].$post({ json: p })
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
    getPlayablePast: (p: { page?: number; state?: string }) =>
      client()
        .api.public["past-events"].$get({
          query: {
            page: p.page?.toString(),
            state: p.state,
          },
        })
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
    // TODO - this isn't used anywhere in the web app.
    // Consider removing.
    getHighestReward: () =>
      client()
        .api.public["highest-reward-event"].$get()
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
    admin: {
      copyEvent: (p: { eventId: number }) =>
        client()
          .api.admin["events"][":eventId"].copy.$post({
            param: { eventId: `${p.eventId}` },
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
      getCurrent: (p: { page?: number }) =>
        client()
          .api.admin["events"].$get({
            query: {
              page: p.page?.toString(),
              statuses: [
                "draft",
                "published",
                "locking",
                "locked",
                "completing",
              ],
            },
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
      getPast: (p: { page?: number }) =>
        client()
          .api.admin["past-events"].$get({
            query: {
              page: p.page?.toString(),
            },
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
      getPlayablePastEvents: (p: { page?: number; state?: string }) =>
        client()
          .api.admin["playable-past-events"].$get({
            query: {
              page: p.page?.toString(),
              state: p.state,
            },
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
    },
    byId: (eventId: number) => ({
      admin: {
        getEventCoupons: (p: { past?: boolean }) =>
          client()
            .api.admin.coupons.extended.$get({
              query: {
                eventId: `${eventId}`,
                past: p.past ? "1" : undefined,
              },
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
        getParticipants: (p: { test?: boolean }) =>
          client()
            .api.admin.events[":eventId"].teams.$get({
              param: { eventId: `${eventId}` },
              query: {
                test: p.test ? "1" : undefined,
              },
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
        getNonParticipants: () =>
          client()
            .api.admin.events[":eventId"]["recent-non-registered-members"].$get(
              {
                param: { eventId: `${eventId}` },
              }
            )
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
        getPastEventParticipants: () =>
          client()
            .api.admin["past-events"][":pastEventId"].teams.$get({
              param: { pastEventId: `${eventId}` },
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
        updateParticipant: (
          p: { teamId: number } & InferRequestType<
            APIEndpoint["admin"]["events"][":eventId"]["teams"][":teamId"]["$patch"]
          >["json"]
        ) =>
          client()
            .api.admin.events[":eventId"].teams[":teamId"].$patch({
              param: {
                eventId: `${eventId}`,
                teamId: `${p.teamId}`,
              },
              json: p,
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
        updatePrizes: (
          p: InferRequestType<
            APIEndpoint["admin"]["events"][":eventId"]["prizes"]["$put"]
          >["json"]
        ) =>
          client()
            .api.admin.events[":eventId"].prizes.$put({
              param: { eventId: `${eventId}` },
              json: p,
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
        makePlayable: (
          p: InferRequestType<
            APIEndpoint["admin"]["events"][":eventId"]["make-playable"]["$post"]
          >["json"]
        ) =>
          client()
            .api.admin.events[":eventId"]["make-playable"].$post({
              param: { eventId: `${eventId}` },
              json: p,
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),

        editPastEvent: (
          p: InferRequestType<
            APIEndpoint["admin"]["past-events"][":pastEventId"]["$patch"]
          >["json"]
        ) =>
          client()
            .api.admin["past-events"][":pastEventId"].$patch({
              param: { pastEventId: `${eventId}` },
              json: p,
            })
            .then(async (r: any) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),

        byTeamId: (teamId: number) => ({
          recalculate: (p: { past?: boolean }) =>
            client()
              // @ts-ignore
              .api.admin.events[":eventId"].teams[":teamId"].recalculate.$post({
                param: { eventId: `${eventId}`, teamId: `${teamId}` },
                json: p,
              })
              .then(async (r: any) => {
                if (r.ok) return r.json();
                throw new Error(r.statusText);
              }),
        }),
      },
      checkInvitationCode: (
        p: InferRequestType<
          APIEndpoint["public"]["events"][":eventId"]["invitation-code-validations"]["$post"]
        >["json"]
      ) =>
        client()
          .api.public.events[":eventId"]["invitation-code-validations"].$post({
            param: { eventId: `${eventId}` },
            json: p,
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
      get: () =>
        client()
          .api.public.events[":eventId"].$get({
            param: { eventId: `${eventId}` },
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
      getPastEvent: () =>
        client()
          .api.public["past-events"][":pastEventId"].$get({
            param: { pastEventId: `${eventId}` },
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
      getPastEventCachedPaths: () =>
        client()
          .api.public["past-events"][":pastEventId"]["num-cached-paths"].$get({
            param: { pastEventId: `${eventId}` },
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
      getTeams: (p: { page?: number; query?: string }) =>
        client()
          .api.public.events[":eventId"].teams.$get({
            param: { eventId: `${eventId}` },
            query: {
              page: p.page?.toString(),
              query: p.query,
            },
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
      getPastEventTeams: () =>
        client()
          .api.public["past-events"][":pastEventId"].teams.$get({
            param: { pastEventId: `${eventId}` },
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
      update: (
        p: InferRequestType<
          APIEndpoint["admin"]["events"][":eventId"]["$patch"]
        >["json"]
      ) =>
        client()
          .api.admin.events[":eventId"].$patch({
            param: { eventId: `${eventId}` },
            json: p,
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
      lock: () =>
        client()
          .api.admin.events[":eventId"].lock.$post({
            param: { eventId: `${eventId}` },
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
      test: () =>
        client()
          // @ts-ignore
          .api.admin.events[":eventId"].test.$post({
            param: { eventId: `${eventId}` },
          })
          .then(async (r: any) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
      complete: () =>
        client()
          .api.admin.events[":eventId"].complete.$post({
            param: { eventId: `${eventId}` },
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
      getEventImageUploadUrl: (
        p: InferRequestType<
          APIEndpoint["admin"]["events"][":eventId"]["image-upload-url"]["$post"]
        >["json"]
      ) =>
        client()
          .api.admin.events[":eventId"]["image-upload-url"].$post({
            param: { eventId: `${eventId}` },
            json: p,
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
      startingLocations: {
        get: (p: { pastEventId?: number }) =>
          client()
            .api.admin["starting-locations"].$get({
              query: {
                eventId: p.pastEventId ? undefined : `${eventId}`,
                pastEventId: p.pastEventId ? `${p.pastEventId}` : undefined,
              },
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
        add: (
          p: Omit<
            InferRequestType<
              APIEndpoint["admin"]["starting-locations"]["$post"]
            >["json"],
            "eventId"
          >
        ) =>
          client()
            .api.admin["starting-locations"].$post({
              json: {
                ...p,
                eventId,
              },
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
        update: (
          p: InferRequestType<
            APIEndpoint["admin"]["starting-locations"][":startingLocationId"]["$patch"]
          >["json"]
        ) =>
          client()
            .api.admin["starting-locations"][":startingLocationId"].$patch({
              param: { startingLocationId: p.id.toString() },
              json: p,
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
        delete: (p: { id: number }) =>
          client()
            .api.admin["starting-locations"][":startingLocationId"].$delete({
              param: { startingLocationId: p.id.toString() },
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
      },
      coupons: {
        get: (p: { past?: boolean | undefined }) =>
          client()
            .api.admin.coupons.$get({
              query: {
                past: p.past ? "1" : undefined,
                eventId: eventId.toString(),
              },
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
        update: (
          p: InferRequestType<APIEndpoint["admin"]["coupons"]["$put"]>["json"]
        ) =>
          client()
            .api.admin.coupons.$put({
              json: p,
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
        updateImage: (
          p: InferRequestType<
            APIEndpoint["admin"]["coupons"][":couponID"]["image"]["$patch"]
          >["json"]
        ) =>
          client()
            .api.admin.coupons[":couponID"].image.$patch({
              // Right now, each event has 0-1 coupons
              // this probs could be changed down the line
              param: { couponID: `${eventId}` },
              json: p,
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
      },
      discounts: {
        get: (p: { past?: boolean }) =>
          client()
            .api.admin.discounts.$get({
              query: {
                eventId: p.past ? undefined : `${eventId}`,
                pastEventId: p.past ? `${eventId}` : undefined,
              },
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
        update: (
          p: { id: number } & InferRequestType<
            APIEndpoint["admin"]["discounts"][":discountId"]["$patch"]
          >["json"]
        ) =>
          client()
            .api.admin.discounts[":discountId"].$patch({
              param: { discountId: p.id.toString() },
              json: p,
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
        delete: (p: { id: number }) =>
          client()
            .api.admin.discounts[":discountId"].$delete({
              param: { discountId: `${p.id}` },
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
        create: (
          p: Omit<
            InferRequestType<
              APIEndpoint["admin"]["discounts"]["$post"]
            >["json"],
            "eventId"
          >
        ) =>
          client()
            .api.admin.discounts.$post({
              json: {
                ...p,
                eventId,
              },
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
        verify: (
          p: InferRequestType<
            APIEndpoint["user"]["events"][":eventId"]["discount-validations"]["$post"]
          >["json"]
        ) =>
          client()
            .api.user.events[":eventId"]["discount-validations"].$post({
              param: { eventId: `${eventId}` },
              json: p,
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
      },
      announcements: {
        get: (p: { past?: boolean }) =>
          client()
            .api.admin.announcements.$get({
              query: {
                eventId: p.past ? undefined : `${eventId}`,
                pastEventId: p.past ? `${eventId}` : undefined,
              },
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
        update: (
          p: { id: number } & InferRequestType<
            APIEndpoint["admin"]["announcements"][":announcementId"]["$patch"]
          >["json"]
        ) =>
          client()
            .api.admin.announcements[":announcementId"].$patch({
              param: { announcementId: p.id.toString() },
              json: p,
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
        delete: (p: { id: number }) =>
          client()
            .api.admin.announcements[":announcementId"].$delete({
              param: { announcementId: `${p.id}` },
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
        create: (
          p: InferRequestType<
            APIEndpoint["admin"]["announcements"]["$post"]
          >["json"]
        ) =>
          client()
            .api.admin.announcements.$post({
              json: {
                ...p,
                eventId,
              },
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
      },
      puzzles: {
        create: (
          p: InferRequestType<APIEndpoint["admin"]["puzzles"]["$post"]>["json"]
        ) =>
          client()
            .api.admin.puzzles.$post({
              json: p,
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
        uploadDiversionImage: (
          p: Omit<
            InferRequestType<
              APIEndpoint["admin"]["puzzles"]["-"]["diversion"]["image"]["$post"]
            >["json"],
            "eventId"
          >
        ) =>
          client()
            .api.admin.puzzles["-"].diversion.image.$post({
              json: {
                ...p,
                eventId,
              },
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
        getAll: (p: { pastEventId?: number }) =>
          client()
            .api.admin.puzzles.$get({
              query: {
                eventId: `${eventId}`,
                pastEventId: p.pastEventId ? `${p.pastEventId}` : undefined,
              },
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
        getMatrix: (p: { refresh?: boolean }) =>
          client()
            .api.admin["distance-matrices"][":eventId"].$get({
              param: { eventId: `${eventId}` },
              query: {
                refresh: p.refresh ? "1" : undefined,
              },
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
        editMatrix: (
          p: InferRequestType<
            APIEndpoint["admin"]["distance-matrices"][":eventId"]["$patch"]
          >["json"]
        ) =>
          client().api.admin["distance-matrices"][":eventId"].$patch({
            param: { eventId: `${eventId}` },
            json: p,
          }),
        getPastMatrix: (p: { refresh?: boolean }) =>
          client()
            .api.admin["past-distance-matrices"][":eventId"].$get({
              param: { eventId: `${eventId}` },
              query: {
                refresh: p.refresh ? "1" : undefined,
              },
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
        editPastMatrix: (
          p: InferRequestType<
            APIEndpoint["admin"]["past-distance-matrices"][":eventId"]["$patch"]
          >["json"]
        ) =>
          client()
            .api.admin["past-distance-matrices"][":eventId"].$patch({
              param: { eventId: `${eventId}` },
              json: p,
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
        byId: (puzzleId: number) => ({
          update: (
            p: InferRequestType<
              APIEndpoint["admin"]["puzzles"][":puzzleId"]["$patch"]
            >["json"]
          ) =>
            client()
              .api.admin.puzzles[":puzzleId"].$patch({
                param: { puzzleId: `${puzzleId}` },
                json: p,
              })
              .then(async (r) => {
                if (r.ok) return r.json();
                throw new Error(r.statusText);
              }),
          delete: (p: { pastEventId?: number }) =>
            client()
              .api.admin.puzzles[":puzzleId"].$delete({
                query: {
                  pastEventId: p.pastEventId ? `${p.pastEventId}` : undefined,
                  eventId: p.pastEventId ? undefined : `${eventId}`,
                },
                param: { puzzleId: `${puzzleId}` },
              })
              .then(async (r) => {
                if (r.ok) return r.json();
                throw new Error(r.statusText);
              }),
          getPuzzleImageUploadUrl: (
            p: InferRequestType<
              APIEndpoint["admin"]["puzzles"][":puzzleId"]["image"]["$post"]
            >["json"]
          ) =>
            client()
              .api.admin.puzzles[":puzzleId"].image.$post({
                param: { puzzleId: `${puzzleId}` },
                json: p,
              })
              .then(async (r) => {
                if (r.ok) return r.json();
                throw new Error(r.statusText);
              }),
          editLocationDiversion: (
            p: InferRequestType<
              APIEndpoint["admin"]["puzzles"][":puzzleId"]["diversion-location"]["$patch"]
            >["json"]
          ) =>
            client()
              .api.admin.puzzles[":puzzleId"]["diversion-location"].$patch({
                param: { puzzleId: `${puzzleId}` },
                json: p,
              })
              .then(async (r) => {
                if (r.ok) return r.json();
                throw new Error(r.statusText);
              }),
          editDigitalCouponDiversion: (
            p: InferRequestType<
              APIEndpoint["admin"]["puzzles"][":puzzleId"]["diversion"]["$patch"]
            >["json"]
          ) =>
            client()
              .api.admin.puzzles[":puzzleId"].diversion.$patch({
                param: { puzzleId: `${puzzleId}` },
                json: p,
              })
              .then(async (r) => {
                if (r.ok) return r.json();
                throw new Error(r.statusText);
              }),
          deleteDiversion: () =>
            client()
              .api.admin.puzzles[":puzzleId"].diversion.$delete({
                param: { puzzleId: `${puzzleId}` },
              })
              .then(async (r) => {
                if (r.ok) return r.json();
                throw new Error(r.statusText);
              }),
        }),
      },
    }),
  },
  eventSeasons: {
    getRecent: () =>
      client()
        .api.public["recent-event-seasons"].$get()
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
    admin: {
      getCurrent: (p: { page?: number }) =>
        client()
          .api.admin["event-seasons"].$get({
            query: {
              page: p.page?.toString(),
            },
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
      create: (
        p: InferRequestType<
          APIEndpoint["admin"]["event-seasons"]["$post"]
        >["json"]
      ) =>
        client()
          .api.admin["event-seasons"].$post({ json: p })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
    },
    byId: (seasonId: number) => ({
      get: () =>
        client()
          .api.public["event-seasons"][":seasonId"].$get({
            param: { seasonId: `${seasonId}` },
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
      admin: {
        updatePrizes: (
          p: InferRequestType<
            APIEndpoint["admin"]["event-seasons"][":seasonId"]["prizes"]["$put"]
          >["json"]
        ) =>
          client()
            .api.admin["event-seasons"][":seasonId"].prizes.$put({
              param: { seasonId: `${seasonId}` },
              json: p,
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
      },

      update: (
        p: InferRequestType<
          APIEndpoint["admin"]["event-seasons"][":seasonId"]["$patch"]
        >["json"]
      ) =>
        client()
          .api.admin["event-seasons"][":seasonId"].$patch({
            param: { seasonId: `${seasonId}` },
            json: p,
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
    }),
  },
  teams: {
    top: () =>
      client()
        .api.public.leaderboard.$get()
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
    // TODO - consider moving these in a better way.
    // List lists all teams
    list: (p: { page?: number; query?: string }) =>
      client()
        .api.public.teams.$get({
          query: {
            page: p.page?.toString(),
            query: p.query,
          },
        })
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
    // Get retrieves MY teams
    get: () =>
      client()
        .api.user.teams.$get()
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
    create: (
      p: InferRequestType<APIEndpoint["user"]["teams"]["$post"]>["json"]
    ) =>
      client()
        .api.user.teams.$post({
          json: p,
        })
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(await r.text());
        }),
    admin: {
      list: (p: { page?: number; query?: string }) =>
        client()
          .api.admin.teams.$get({
            query: {
              page: p.page?.toString(),
              query: p.query,
            },
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
      byId: (teamId: number) => ({
        getTeamReport: () =>
          client()
            .api.admin.teams[":teamId"].report.$get({
              param: { teamId: `${teamId}` },
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
      }),
    },
    byId: (teamId: number) => ({
      addMutualStartRequest: (
        p: InferRequestType<
          APIEndpoint["user"]["teams"][":teamId"]["mutual-start-requests"]["$post"]
        >["json"]
      ) =>
        client()
          .api.user.teams[":teamId"]["mutual-start-requests"].$post({
            param: { teamId: `${teamId}` },
            json: p,
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
      getMutualStartRequests: (p: { eventId: number }) =>
        client()
          .api.user.teams[":teamId"]["mutual-start-requests"].$get({
            param: { teamId: `${teamId}` },
            query: { eventId: p.eventId.toString() },
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
      get: () =>
        client()
          .api.public.teams[":teamId"].$get({
            param: { teamId: `${teamId}` },
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
      getUpcomingEvents: () =>
        client()
          .api.user.teams[":teamId"]["upcoming-events"].$get({
            param: { teamId: `${teamId}` },
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),

      retire: () =>
        client()
          .api.user.teams[":teamId"].$delete({
            param: { teamId: `${teamId}` },
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            const message = await r.text();
            throw new Error(message);
          }),
      addMember: (
        p: InferRequestType<
          APIEndpoint["user"]["teams"][":teamId"]["members"]["$post"]
        >["json"]
      ) =>
        client()
          .api.user.teams[":teamId"].members.$post({
            param: { teamId: `${teamId}` },
            json: p,
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            const errorMessage = await r.text();
            throw new Error(errorMessage);
          }),
      removeAllMembers: () =>
        // TODO - This should likely just be client().api.user.teams[":teamId"].members.$delete
        client()
          .api.user.teams[":teamId"].members[":userId"].$delete({
            query: {
              removeAll: "1",
            },
            param: {
              teamId: `${teamId}`,
              userId: "-",
            },
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(r.statusText);
          }),
      member: (userId: number) => ({
        remove: () =>
          client()
            .api.user.teams[":teamId"].members[":userId"].$delete({
              query: {
                removeAll: undefined,
              },
              param: {
                teamId: `${teamId}`,
                userId: `${userId}`,
              },
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
      }),
      /**
       * getJoinEvent - check if a team can join an event
       */
      getJoinEvent: (p: { eventId: number }) =>
        client()
          .api.user.teams[":teamId"]["join-event"].$get({
            param: { teamId: `${teamId}` },
            query: { eventId: `${p.eventId}` },
          })
          .then(async (r) => {
            if (r.ok) {
              return r.json();
            }
            const message = await r.text();
            throw new Error(message);
          }),
      /**
       * postJoinEvent - join an event
       */
      postJoinEvent: (
        p: InferRequestType<
          APIEndpoint["user"]["teams"][":teamId"]["join-event"]["$post"]
        >["json"]
      ) =>
        client()
          .api.user.teams[":teamId"]["join-event"].$post({
            param: { teamId: `${teamId}` },
            json: p,
          })
          .then(async (r) => {
            if (r.ok) {
              return r.json();
            }
            const message = await r.text();
            throw new Error(message);
          }),
      pastEventRegistration: (
        p: InferRequestType<
          APIEndpoint["user"]["teams"][":teamId"]["join-past-event"]["$post"]
        >["json"]
      ) =>
        client()
          .api.user.teams[":teamId"]["join-past-event"].$post({
            param: { teamId: `${teamId}` },
            json: p,
          })
          .then(async (r) => {
            if (r.ok) return r.json();
            throw new Error(await r.text());
          }),
      captainTransferRequest: {
        create: (
          p: InferRequestType<
            APIEndpoint["user"]["teams"][":teamId"]["captain-transfer"]["$put"]
          >["json"]
        ) =>
          client()
            .api.user.teams[":teamId"]["captain-transfer"].$put({
              param: { teamId: `${teamId}` },
              json: p,
            })
            .then(async (r) => {
              if (r.ok) return r.json();
              throw new Error(r.statusText);
            }),
      },
    }),
  },
  payments: {
    // This api appears to handle both events + products
    // There was another route to handle just products active in the backend
    // but it wasn't used here. Is it deprecated and just not removed yet?
    // If so, let's make this a single $post to /payments.
    createIntent: (
      p: InferRequestType<
        APIEndpoint["user"]["payments"]["events"]["$post"]
      >["json"]
    ) =>
      client()
        .api.user.payments.events.$post({
          json: p,
        })
        .then(async (r) => {
          if (r.ok) return r.json();
          throw new Error(r.statusText);
        }),
  },
});

export const api = createApiRoutes();
